import { useTranslation } from "react-i18next";
import React from 'react';
import { Card, Descriptions, Typography, Divider, Tag, Avatar } from 'antd';
import { formatDateTime } from '../../../../utils/dateUtils';
import { UserOutlined } from '@ant-design/icons';
import { Message } from '../../../../types/messageTypes';

interface MessageDetailProps {
  message: Message;
  onBack: () => void;
}

const { Title, Paragraph } = Typography;

const MessageDetail: React.FC<MessageDetailProps> = ({ message, onBack }) => {
  const { t } = useTranslation();

  return (
    <div className="">
      <div className="bg-white shadow rounded-3 p-0">
        {/* Header */}
        <div className="d-flex align-items-center border-bottom px-4 py-3 position-relative" style={{ background: '#f5f7fa', borderTopLeftRadius: 12, borderTopRightRadius: 12, minHeight: 56 }}>
          <span
            className="ti ti-arrow-left fs-4 text-primary position-absolute"
            style={{ left: 20, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
            onClick={onBack}
            title={t('back')}
            role="button"
            tabIndex={0}
          />
          <div className="d-flex align-items-center mx-auto" style={{ marginLeft: 32 }}>
            <Avatar icon={<UserOutlined />} size={48} className="me-3" />
            <div>
              <div className="fw-bold" style={{ fontSize: 18 }}>{message.sender?.name}</div>
              <div className="text-muted" style={{ fontSize: 13 }}>{message.sender?.email}</div>
            </div>
          </div>
        </div>

        {/* Subject */}
        <div className="px-4 pt-3 pb-1">
          <Title level={4} className="mb-1" style={{ marginBottom: 0 }}>{message.subject}</Title>
          <div className="d-flex align-items-center text-muted" style={{ fontSize: 13 }}>
            <span className="me-2">{t('from')}: {message.sender?.name}</span>
            <span className="me-2">|</span>
            <span>{t('to')}: {message.recipients.map(r => r.name).join(', ')}</span>
            <span className="ms-auto">{t('sent_at')}: {formatDateTime(message.timestamp)}</span>
          </div>
        </div>
        <Divider className="my-2" style={{ margin: 0 }} />

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="px-4 pt-2 pb-0">
            <div className="d-flex align-items-center mb-2">
              <span className="me-2" style={{ fontSize: 16 }} role="img" aria-label="paperclip">📎</span>
              <span className="fw-semibold">{t('attachments')}</span>
            </div>
            <ul className="list-unstyled mb-2">
              {message.attachments.map((attachment, idx) => (
                <li key={idx} className="mb-1">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={attachment.url}
                    download
                    style={{ textDecoration: 'none', color: '#1677ff', fontWeight: 500 }}
                  >
                    {attachment.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Body */}
        <div className="px-4 pt-3 pb-4">
          <Title level={5} className="mb-2">{t("message_body")}</Title>
          <div className="gmail-message-body" style={{ background: '#f9fafb', borderRadius: 8, padding: 16, minHeight: 120 }}
            dangerouslySetInnerHTML={{ __html: message.body }}
          />
        </div>
      </div>
    </div>
  );
};

export default MessageDetail;
